import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown } from "lucide-react";
import { getLocalePath, type SupportedLocale } from "@/i18n/routing";

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages: Array<{ code: SupportedLocale; name: string }> = [
    { code: 'et', name: 'Eesti' },
    { code: 'en', name: 'English' },
    { code: 'ru', name: 'Русский' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.resolvedLanguage) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef} data-testid="language-switcher">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors duration-200"
        aria-expanded={isOpen}
        data-testid="language-button"
      >
        <Globe className="w-4 h-4" aria-hidden="true" />
        <span>{currentLanguage.code.toUpperCase()}</span>
        <ChevronDown className="w-3 h-3" aria-hidden="true" />
      </button>

      <div
        className="absolute right-0 mt-2 w-32 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50"
        hidden={!isOpen}
      >
          <div className="py-1">
            {languages.map((language) => (
              <a
                key={language.code}
                href={getLocalePath(language.code)}
                hrefLang={language.code}
                onClick={() => setIsOpen(false)}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                data-testid={`language-option-${language.code}`}
              >
                {language.name}
              </a>
            ))}
          </div>
        </div>
    </div>
  );
}
